import HotelService from "../../../services/hotel.service";
import React, { useState, useRef } from "react";
import { Box, Button, Layer, RadioButtonGroup, Text } from "grommet";
import { View } from "grommet-icons";
import "react-quill/dist/quill.snow.css";
import { CustomTabs } from "@features/shared/CustomTabs";
import CheckinFlowService from "../../../services/checkin-flow.service";
import TagTextInput from "@features/shared/TagTextInput";
import TagQuillEditor from "@features/shared/TagQuillEditor";
import { normalizeTagSpacing } from "@features/shared/normalizeTagSpacing";
import { useTranslation } from "react-i18next";
import { useFetchHotel } from "../redux/hooks";
import { getOfferCapabilities } from "../../../common/offerCapabilities";
import { PackageUpgradeModal } from "./OfferGate";
import EmailUpsellsModal from "./EmailUpsellsModal";

const updateUpsellsTag = (email, hasUpsells) => {
  const updateBody = (body = "") => {
    const withoutUpsells = body.replace(/<p>\s*\{upsells\}\s*<\/p>/g, "");
    if (!hasUpsells) return withoutUpsells;

    for (const tag of [
      "mobileAppGuide",
      "downloadAppButton",
      "RoomsAndCodes",
    ]) {
      const tagIndex = withoutUpsells.indexOf(`{${tag}}`);
      if (tagIndex < 0) continue;
      const paragraphEnd = withoutUpsells.indexOf("</p>", tagIndex);
      if (paragraphEnd >= 0) {
        const insertAt = paragraphEnd + 4;
        return `${withoutUpsells.slice(0, insertAt)}<p>{upsells}</p>${withoutUpsells.slice(insertAt)}`;
      }
    }

    return `${withoutUpsells}<p>{upsells}</p>`;
  };

  return {
    ...email,
    body: {
      en: updateBody(email?.body?.en),
      fr: updateBody(email?.body?.fr),
    },
  };
};

export default function LocalizedEmailEditor({
  value = {
    subject: { en: "", fr: "" },
    title: { en: "", fr: "" },
    body: { en: "", fr: "" },
  },
  onChange,
  defaultValue,
  useCustom,
  onUseCustomChange,
  hideUseCustomToggle,
  template,
  tagHint,
  tags,
  upsells = [],
  onUpsellsChange,
  currency,
}) {
  const { t } = useTranslation();
  const inlineTags = tags?.filter(tag => !["precheckinButton", "stayDatesBox", "mobileAppGuide", "upsells", "RoomsAndCodes", "downloadAppButton"].includes(tag));
  const currentValue = useRef(value);
  currentValue.current = value;
  const updateValue = next => { currentValue.current = next; onChange(next); };
  const [activeLang, setActiveLang] = useState(0);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLang, setPreviewLang] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const previewRequest = useRef(0);
  const closePreview = () => { previewRequest.current += 1; setPreviewHtml(null); setPreviewLoading(false); };
  const { hotel } = useFetchHotel();
  const [resolvedUpsells, setResolvedUpsells] = useState(null);
  const [upsellsLoading, setUpsellsLoading] = useState(false);
  const [upsellsError, setUpsellsError] = useState(false);
  const upsellsEnabled = resolvedUpsells ?? hotel?.offerCapabilities?.upsells ?? getOfferCapabilities(hotel).upsells;
  const openUpsells = async () => {
    setUpsellsLoading(true);
    setUpsellsError(false);
    try {
      const response = await HotelService.getHotel();
      if (!response?.data) return;
      const freshHotel = response.data;
      setResolvedUpsells(freshHotel.offerCapabilities?.upsells ?? getOfferCapabilities(freshHotel).upsells);
      setShowUpsells(true);
    } catch {
      setUpsellsError(true);
    } finally {
      setUpsellsLoading(false);
    }
  };
  const [showUpsells, setShowUpsells] = useState(false);

  const handleSubjectChange = (lang, val) => {
    updateValue({ ...currentValue.current, subject: { ...currentValue.current.subject, [lang]: val } });
  };

  const handleTitleChange = (lang, val) => {
    updateValue({ ...currentValue.current, title: { ...currentValue.current.title, [lang]: val } });
  };

  const handleBodyChange = (lang, val) => {
    updateValue({ ...currentValue.current, body: { ...currentValue.current.body, [lang]: val } });
  };

  const handleBlur = (field, lang, freshText) => {
    const current =
      freshText !== undefined ? freshText : value?.[field]?.[lang];
    const normalized = normalizeTagSpacing(current, lang);
    if (normalized !== current) {
      updateValue({ ...currentValue.current, [field]: { ...currentValue.current[field], [lang]: normalized } });
    }
  };

  const loadPreview = async lang => {
    // Capture the current draft for this request, including edits committed on blur.
    const source = useCustom ? currentValue.current : defaultValue;
    const draft = {
      subject: source?.subject?.[lang] || "",
      title: source?.title?.[lang] || "",
      body: source?.body?.[lang] || "",
    };
    const request = ++previewRequest.current;
    setPreviewLoading(true);
    setPreviewError(false);
    setPreviewHtml(null);
    try {
      const res = await CheckinFlowService.previewEmail(draft.subject, draft.title, draft.body, lang, template, upsells);
      if (request !== previewRequest.current) return;
      if (!res.data?.html) throw new Error("Empty preview response");
      setPreviewHtml(res.data.html);
      setPreviewLang(lang === "fr" ? 1 : 0);
    } catch {
      if (request === previewRequest.current) setPreviewError(true);
    } finally {
      if (request === previewRequest.current) setPreviewLoading(false);
    }
  };

  const handlePreviewClick = () => {
    loadPreview(activeLang === 0 ? "en" : "fr");
  };

  const handlePreviewLangChange = (langIdx) => {
    setPreviewLang(langIdx);
    loadPreview(langIdx === 0 ? "en" : "fr");
  };

  const tabs = [
    {
      title: t("common.english"),
      key: "en",
      content: (
        <Box gap="small">
          <Box direction="row" align="center" gap="small">
            <Text size="small" weight="bold" style={{ whiteSpace: "nowrap" }}>
              {t("localizedEmail.subject")}:
            </Text>
            <TagTextInput
              placeholder={t("localizedEmail.subjectPlaceholder")}
              value={value.subject?.en || ""}
              onChange={(val) => handleSubjectChange("en", val)}
              onBlur={(freshText) => handleBlur("subject", "en", freshText)}
              tags={inlineTags}
            />
          </Box>
          <Box direction="row" align="center" gap="small">
            <Text size="small" weight="bold" style={{ whiteSpace: "nowrap" }}>
              {t("localizedEmail.title")}:
            </Text>
            <TagTextInput
              placeholder={t("localizedEmail.titlePlaceholder")}
              value={value.title?.en || ""}
              onChange={(val) => handleTitleChange("en", val)}
              onBlur={(freshText) => handleBlur("title", "en", freshText)}
              tags={inlineTags}
            />
          </Box>
          <Box className="checkin-flow-quill-wrapper">
            <TagQuillEditor
              value={value.body?.en || ""}
              onChange={(val) => handleBodyChange("en", val)}
              onBlur={(freshText) => handleBlur("body", "en", freshText)}
              tags={tags}
            />
          </Box>
        </Box>
      ),
    },
    {
      title: t("common.french"),
      key: "fr",
      content: (
        <Box gap="small">
          <Box direction="row" align="center" gap="small">
            <Text size="small" weight="bold" style={{ whiteSpace: "nowrap" }}>
              Objet :
            </Text>
            <TagTextInput
              placeholder={t("localizedEmail.subjectPlaceholderFr")}
              value={value.subject?.fr || ""}
              onChange={(val) => handleSubjectChange("fr", val)}
              onBlur={(freshText) => handleBlur("subject", "fr", freshText)}
              tags={inlineTags}
            />
          </Box>
          <Box direction="row" align="center" gap="small">
            <Text size="small" weight="bold" style={{ whiteSpace: "nowrap" }}>
              Titre :
            </Text>
            <TagTextInput
              placeholder={t("localizedEmail.titlePlaceholderFr")}
              value={value.title?.fr || ""}
              onChange={(val) => handleTitleChange("fr", val)}
              onBlur={(freshText) => handleBlur("title", "fr", freshText)}
              tags={inlineTags}
            />
          </Box>
          <Box className="checkin-flow-quill-wrapper">
            <TagQuillEditor
              value={value.body?.fr || ""}
              onChange={(val) => handleBodyChange("fr", val)}
              onBlur={(freshText) => handleBlur("body", "fr", freshText)}
              tags={tags}
            />
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <Box gap="small">
      <Box className="localized-email-actions" direction="row" gap="medium" align="center" wrap>
        {!hideUseCustomToggle && (
          <RadioButtonGroup
            name="emailMode"
            direction="row"
            gap="medium"
            options={[
              { label: t("localizedEmail.useDefault"), value: "default" },
              { label: t("localizedEmail.custom"), value: "custom" },
            ]}
            value={useCustom ? "custom" : "default"}
            onChange={(e) => onUseCustomChange(e.target.value === "custom")}
          />
        )}
        <Button
          icon={<View size="small" />}
          label={
            previewLoading
              ? t("localizedEmail.loading")
              : t("localizedEmail.preview")
          }
          size="small"
          onClick={handlePreviewClick}
          disabled={previewLoading}
        />
        {onUpsellsChange && (
          <Button
            label={t("emailUpsells.configure", { count: upsells.length })}
            size="small"
            onClick={openUpsells}
            disabled={upsellsLoading}
          />
        )}
        {useCustom && defaultValue && (
          <Button
            plain
            label={
              <Text size="small" color="brand">
                {t("localizedEmail.revertToDefault")}
              </Text>
            }
            onClick={() => {
              if (window.confirm(t("localizedEmail.revertToDefaultConfirm"))) {
                onChange(defaultValue);
              }
            }}
          />
        )}
      </Box>

      {previewError && <Text role="alert" color="status-critical">{t("localizedEmail.previewError")}</Text>}
      {upsellsError && <Text role="alert" color="status-critical">{t("emailUpsells.loadError")}</Text>}
      {useCustom && (
        <>
          {tagHint && (
            <Text
              size="xsmall"
              color="black"
              style={{ fontStyle: "italic", marginBottom: "8px" }}
            >
              {tagHint}
            </Text>
          )}
          <CustomTabs
            tabs={tabs}
            activeIndex={activeLang}
            onTabChange={setActiveLang}
          />
        </>
      )}

      {previewHtml && (
        <Layer
          onEsc={closePreview}
          onClickOutside={closePreview}
          responsive
          margin="medium"
        >
          <Box width={{ max: "750px", min: "650px" }} height={{ max: "90vh" }}>
            <Box
              direction="row"
              justify="between"
              align="center"
              pad={{ horizontal: "medium", vertical: "small" }}
              border={{ side: "bottom", color: "light-4" }}
              flex={false}
            >
              <Box direction="row" gap="small" align="center">
                <Text weight="bold">{t("localizedEmail.emailPreview")}</Text>
                <CustomTabs
                  tabs={[
                    { title: t("common.english"), key: "en" },
                    { title: t("common.french"), key: "fr" },
                  ]}
                  activeIndex={previewLang}
                  onTabChange={handlePreviewLangChange}
                />
              </Box>
              <Button
                label={t("localizedEmail.close")}
                size="small"
                onClick={closePreview}
              />
            </Box>
            <Box flex overflow="auto" pad="none">
              <iframe
                srcDoc={previewHtml}
                title={t("localizedEmail.emailPreview")}
                sandbox=""
                style={{
                  width: "100%",
                  height: "100%",
                  minHeight: 500,
                  border: "none",
                }}
              />
            </Box>
          </Box>
        </Layer>
      )}

      {showUpsells && !upsellsEnabled && <PackageUpgradeModal capability="upsells" onClose={() => setShowUpsells(false)} />}
      {showUpsells && upsellsEnabled && (
        <EmailUpsellsModal
          selections={upsells}
          currency={currency}
          onClose={() => setShowUpsells(false)}
          onSave={(nextUpsells) => {
            const nextValue = updateUpsellsTag(value, nextUpsells.length > 0);
            onUpsellsChange(nextUpsells, nextValue);
            setShowUpsells(false);
          }}
        />
      )}
    </Box>
  );
}
